// Payment panel — right-column inline state machine: summary → cash → qr.
// Replaces PaymentSummary + PaymentCashDialog + PaymentQRDialog overlays.
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PaymentCashSection } from "./payment-cash-section";
import { PaymentMixedSection } from "./payment-mixed-section";
import { PaymentQRSection } from "./payment-qr-section";
import { CustomerSelect } from "./customer-select";
import type { Order, PaymentPart } from "./use-pos-cart";

export type PaymentView = "summary" | "cash" | "qr" | "bank" | "mixed";

// Bật QR VietQR (SePay). Tạm ẩn vì chưa cấu hình SePay — dùng "Chuyển khoản (F7)"
// thủ công thay thế. Khi cấu hình SePay xong, đổi thành true để hiện lại nút + phím F8.
const QR_ENABLED = false;

function fmtVND(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
}

export interface PaymentPanelProps {
  order: Order | null;
  coupon: { code: string; amountOff: number } | null;
  onDiscount: (amount: string) => void;
  /** Lưu tên NV bán hàng (gõ tay, in lên hóa đơn). */
  onSalesperson: (name: string) => void;
  onPayCash: (tenderedAmount: number, couponCode?: string, reason?: string) => void;
  onPayBank: (tenderedAmount: number, couponCode?: string, reason?: string) => void;
  onPayMixed: (payments: PaymentPart[], couponCode?: string, reason?: string) => void;
  onPayQR: () => void; // reserved for API compat
  onQRSuccess: () => void;
  onNewOrder: () => void;
  loading?: boolean;
  view: PaymentView;
  onViewChange: (view: PaymentView) => void;
  // Customer selector is rendered at the top of the summary view only when
  // onSelectCustomer is provided. Desktop wires it here; mobile keeps its own
  // toolbar chip and omits these so the selector is not shown twice.
  selectedCustomerId?: string | null;
  onSelectCustomer?: (customer: { id: string } | null) => void;
  /** Được phép sửa chiết khấu đơn (cờ phân quyền). Khi false: chỉ hiển thị. */
  canEditPrice?: boolean;
}

export function PaymentPanel({
  order,
  coupon,
  onDiscount,
  onSalesperson,
  onPayCash,
  onPayBank,
  onPayMixed,
  onQRSuccess,
  onNewOrder,
  loading,
  view,
  onViewChange,
  selectedCustomerId,
  onSelectCustomer,
  canEditPrice,
}: PaymentPanelProps) {
  const subtotal = parseFloat(order?.subtotal ?? "0");
  const discountAmount = parseFloat(order?.discountAmount ?? "0");
  const couponAmount = coupon?.amountOff ?? 0;
  const total = Math.max(0, subtotal - discountAmount - couponAmount);
  const hasCustomer = !!order?.customerId;
  const canPay = !!order && total > 0 && order.status !== "paid" && order.status !== "partial_paid";

  const handleDiscount = (e: React.ChangeEvent<HTMLInputElement>) => {
    onDiscount(e.target.value || "0");
  };

  const handleCashConfirm = (tenderedAmount: number, couponCode?: string, reason?: string) => {
    onPayCash(tenderedAmount, couponCode, reason);
    // view reset happens in pos-screen-page after onSuccess
  };

  const handleBankConfirm = (tenderedAmount: number, couponCode?: string, reason?: string) => {
    onPayBank(tenderedAmount, couponCode, reason);
  };

  const handleQRSuccess = () => {
    onQRSuccess();
    onViewChange("summary");
  };

  // QR back: just return to summary (cancel is handled inside PaymentQRSection).
  const handleBack = () => onViewChange("summary");

  if (view === "cash") {
    return (
      <PaymentCashSection
        total={total}
        hasCustomer={hasCustomer}
        couponCode={coupon?.code}
        onConfirm={handleCashConfirm}
        onBack={handleBack}
        loading={loading}
      />
    );
  }

  if (view === "bank") {
    return (
      <PaymentCashSection
        mode="bank"
        total={total}
        hasCustomer={hasCustomer}
        couponCode={coupon?.code}
        onConfirm={handleBankConfirm}
        onBack={handleBack}
        loading={loading}
      />
    );
  }

  if (view === "mixed") {
    return (
      <PaymentMixedSection
        total={total}
        hasCustomer={hasCustomer}
        couponCode={coupon?.code}
        onConfirm={onPayMixed}
        onBack={handleBack}
        loading={loading}
      />
    );
  }

  if (view === "qr" && order) {
    return (
      <PaymentQRSection
        orderId={order.id}
        onSuccess={handleQRSuccess}
        onBack={handleBack}
      />
    );
  }

  // Default: summary view.
  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Customer — grouped with the checkout step for quick access */}
      {onSelectCustomer && (
        <CustomerSelect
          selectedId={selectedCustomerId}
          onSelect={onSelectCustomer}
          mode="inline"
        />
      )}

      {/* NV bán hàng — gõ tay, lưu khi rời ô, in lên hóa đơn. key theo đơn để
          ô tự reset khi chuyển sang đơn khác. */}
      <Input
        key={order?.id ?? "no-order"}
        placeholder="Tên NV bán hàng (in hóa đơn)…"
        defaultValue={order?.salesperson ?? ""}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v !== (order?.salesperson ?? "")) onSalesperson(v);
        }}
        onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()}
        disabled={!order}
        className="text-sm"
      />

      {/* Totals */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between text-muted-foreground">
          <span>Tạm tính</span>
          <span>{fmtVND(subtotal)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground shrink-0">Giảm giá đơn</span>
          {canEditPrice ? (
            <Input
              type="number"
              min={0}
              defaultValue={order?.discountAmount ?? "0"}
              onBlur={handleDiscount}
              onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()}
              className="w-36 text-right font-mono text-sm"
              disabled={!order}
            />
          ) : (
            <span className="font-mono text-sm">{fmtVND(discountAmount)}</span>
          )}
        </div>
        {couponAmount > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Coupon</span>
            <span>-{fmtVND(couponAmount)}</span>
          </div>
        )}
        <div className="flex justify-between border-t pt-2 text-lg font-bold">
          <span>Tổng cộng</span>
          <span className="text-primary">{fmtVND(total)}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="space-y-2">
        <Button
          className="w-full text-base font-semibold"
          disabled={!canPay || loading}
          onClick={() => onViewChange("cash")}
        >
          Tiền mặt (F9)
        </Button>
        <Button
          variant="outline"
          className="w-full font-semibold"
          disabled={!canPay || loading}
          onClick={() => onViewChange("bank")}
        >
          Chuyển khoản (F7)
        </Button>
        <Button
          variant="outline"
          className="w-full font-semibold"
          disabled={!canPay || loading}
          onClick={() => onViewChange("mixed")}
        >
          Tiền mặt + Chuyển khoản (F10)
        </Button>
        {QR_ENABLED && (
          <Button
            variant="outline"
            className="w-full font-semibold"
            disabled={!canPay || loading}
            onClick={() => onViewChange("qr")}
          >
            Chuyển khoản QR (F8)
          </Button>
        )}
        <Button
          variant="outline"
          className="w-full"
          disabled={loading}
          onClick={onNewOrder}
        >
          Đơn mới (F11)
        </Button>
      </div>

      {/* Keyboard hint bar */}
      <div className="mt-2 rounded-md bg-muted p-3 text-xs text-muted-foreground space-y-1">
        <div><kbd className="rounded border px-1">F2</kbd> Tìm sản phẩm</div>
        <div><kbd className="rounded border px-1">F7</kbd> Chuyển khoản</div>
        {QR_ENABLED && <div><kbd className="rounded border px-1">F8</kbd> Thanh toán QR</div>}
        <div><kbd className="rounded border px-1">F9</kbd> Thanh toán tiền mặt</div>
        <div><kbd className="rounded border px-1">F10</kbd> Tiền mặt + Chuyển khoản</div>
        <div><kbd className="rounded border px-1">F11</kbd> Đơn hàng mới</div>
      </div>
    </div>
  );
}
